import type { PropsWithChildren } from 'react';

import { t } from '@nodal/i18n';
import { Alert } from '@nodal/uikit/components/Alert';
import { ButtonLoading } from '@nodal/uikit/components/ButtonLoading';
import { SurveyCard } from '@nodal/uikit/components/SurveyCard';
import { Form, Formik, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import type {
  FormQuestionsProps,
  FormValues,
  QuestionnaireScreen,
} from './FormQuestions.interface';
import type { FormikErrors, FormikTouched } from 'formik';

const isError = <T extends QuestionnaireScreen>(
  field: string,
  errors: FormikErrors<FormValues<T>>,
  touched: FormikTouched<FormValues<T>>,
) => {
  return !!(
    Object.keys(errors).find((e) => e === field) &&
    Object.keys(touched).find((t) => t === field)
  );
};

const getFieldErrorNames = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formikErrors: any,
): string[] => {
  const transformObjectToDotNotation = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    obj: any,
    prefix = '',
    result: string[] = [],
  ): string[] => {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      if (!value) return;

      const nextKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === 'object') {
        transformObjectToDotNotation(value, nextKey, result);
      } else {
        result.push(nextKey);
      }
    });

    return result;
  };

  return transformObjectToDotNotation(formikErrors);
};

const ScrollToFieldError = () => {
  const { submitCount, errors } = useFormikContext();
  const lastProcessedSubmitRef = useRef<number>(0);
  const pendingSubmitRef = useRef<number | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (submitCount > 0 && submitCount !== lastProcessedSubmitRef.current) {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }

      pendingSubmitRef.current = submitCount;
      lastProcessedSubmitRef.current = submitCount;
    }
  }, [submitCount]);

  useEffect(() => {
    if (pendingSubmitRef.current === null || submitCount === 0) {
      return;
    }

    const fieldErrorNames = getFieldErrorNames(errors);

    if (fieldErrorNames.length === 0) {
      return;
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }

    const checkAndScroll = (attempt = 0) => {
      const currentFieldErrorNames = getFieldErrorNames(errors);

      if (currentFieldErrorNames.length === 0 && attempt < 10) {
        const delay =
          attempt < 3 ? 200 : attempt < 6 ? 300 : attempt < 8 ? 400 : 500;
        scrollTimeoutRef.current = setTimeout(() => {
          checkAndScroll(attempt + 1);
        }, delay);
        return;
      }

      if (currentFieldErrorNames.length === 0) {
        pendingSubmitRef.current = null;
        return;
      }

      const currentFirstErrorField = currentFieldErrorNames[0];
      const currentBaseName = currentFirstErrorField.includes('.')
        ? currentFirstErrorField.split('.')[0]
        : currentFirstErrorField;

      const errorCards = document.querySelectorAll('.border-red-500');

      if (errorCards.length === 0 && attempt < 8) {
        const delay = attempt < 3 ? 200 : attempt < 6 ? 300 : 400;
        scrollTimeoutRef.current = setTimeout(() => {
          checkAndScroll(attempt + 1);
        }, delay);
        return;
      }

      if (errorCards.length === 0) {
        const allCards = document.querySelectorAll(
          '.bg-white.rounded-lg.border',
        );
        for (const card of Array.from(allCards)) {
          const hasField =
            (currentFirstErrorField.includes('.') &&
              card.querySelector(`input[name^='${currentBaseName}.']`)) ||
            card.querySelector(`input[name='${currentFirstErrorField}']`) ||
            card.querySelector(
              `input[type="radio"][name='${currentFirstErrorField}']`,
            ) ||
            card.querySelector(`textarea[name='${currentFirstErrorField}']`) ||
            card.querySelector(`select[name='${currentFirstErrorField}']`);

          if (hasField) {
            const targetCard = card as HTMLElement;

            const scrollToCard = () => {
              let element: HTMLElement | null = targetCard;
              let offsetTop = 0;

              while (element) {
                offsetTop += element.offsetTop;
                element = element.offsetParent as HTMLElement | null;
              }

              const cardHeight = targetCard.offsetHeight;
              const viewportHeight = window.innerHeight;

              let targetScrollY: number;
              if (cardHeight < viewportHeight) {
                const headerHeight = 80;
                targetScrollY = offsetTop - headerHeight - 20;
              } else {
                targetScrollY = offsetTop + cardHeight / 2 - viewportHeight / 2;
              }

              window.scrollTo({
                top: Math.max(0, targetScrollY),
                behavior: 'smooth',
              });
            };

            scrollToCard();
            setTimeout(scrollToCard, 50);
            setTimeout(scrollToCard, 200);

            focusTimeoutRef.current = setTimeout(() => {
              focusTimeoutRef.current = null;
              const input = targetCard.querySelector(
                'input, textarea, select, button[role="combobox"]',
              ) as HTMLElement;
              if (input && input.focus) input.focus();
              pendingSubmitRef.current = null;
            }, 400);
            return;
          }
        }
        pendingSubmitRef.current = null;
        return;
      }

      const firstErrorCard = errorCards[0] as HTMLElement;

      const scrollToCard = () => {
        let element: HTMLElement | null = firstErrorCard;
        let offsetTop = 0;

        while (element) {
          offsetTop += element.offsetTop;
          element = element.offsetParent as HTMLElement | null;
        }

        const cardHeight = firstErrorCard.offsetHeight;
        const viewportHeight = window.innerHeight;

        let targetScrollY: number;
        if (cardHeight < viewportHeight) {
          const headerHeight = 80;
          targetScrollY = offsetTop - headerHeight - 20;
        } else {
          targetScrollY = offsetTop + cardHeight / 2 - viewportHeight / 2;
        }

        window.scrollTo({
          top: Math.max(0, targetScrollY),
          behavior: 'smooth',
        });
      };

      scrollToCard();
      setTimeout(scrollToCard, 50);
      setTimeout(scrollToCard, 200);

      focusTimeoutRef.current = setTimeout(() => {
        focusTimeoutRef.current = null;
        let inputElement: HTMLElement | null = null;

        if (currentFirstErrorField.includes('.')) {
          inputElement = firstErrorCard.querySelector(
            `input[name^='${currentBaseName}.']`,
          ) as HTMLElement;
        }

        if (!inputElement) {
          inputElement =
            firstErrorCard.querySelector(
              `input[name='${currentFirstErrorField}']`,
            ) ||
            firstErrorCard.querySelector(
              `input[type="radio"][name='${currentFirstErrorField}']`,
            ) ||
            firstErrorCard.querySelector(
              `textarea[name='${currentFirstErrorField}']`,
            ) ||
            firstErrorCard.querySelector(
              `select[name='${currentFirstErrorField}']`,
            ) ||
            firstErrorCard.querySelector(`button[role="combobox"]`) ||
            (firstErrorCard.querySelector('input') as HTMLElement);
        }

        if (
          inputElement &&
          (inputElement instanceof HTMLInputElement ||
            inputElement instanceof HTMLTextAreaElement ||
            inputElement instanceof HTMLSelectElement ||
            inputElement instanceof HTMLButtonElement)
        ) {
          inputElement.focus();
        }
        pendingSubmitRef.current = null;
      }, 400);
    };

    scrollTimeoutRef.current = setTimeout(() => {
      scrollTimeoutRef.current = null;
      checkAndScroll();
    }, 800);
  }, [submitCount, errors]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  return null;
};

export const FormQuestions = <T extends QuestionnaireScreen>(
  props: PropsWithChildren<FormQuestionsProps<T>>,
) => {
  const {
    title,
    description,
    questions,
    initialValues,
    validationSchema,
    alert,
    onSubmit,
  } = props;

  const shouldRenderSurveyCard = (
    formValues: FormValues<T>,
    hidden?: (value: FormValues<T>) => boolean,
  ) => (hidden ? !hidden(formValues) : true);

  if (alert) {
    return (
      <div className="flex flex-col justify-start items-start w-full lg:w-step-view">
        <div className="pb-6 w-full">
          <Alert {...alert} />
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ errors, touched, values, isSubmitting }) => (
        <Form className="flex flex-col gap-4 justify-start items-start w-full h-full lg:w-step-view">
          <ScrollToFieldError />
          <div
            className={`p-8 w-full text-left bg-beige-500 rounded-lg border border-sand-200`}
          >
            <div className="flex flex-col gap-4">
              <h3 className="text-2xl font-semibold leading-8 text-sand-900">
                {title}
              </h3>
              <p className="text-base font-normal leading-6 text-sand-900">
                {description}
              </p>
            </div>
          </div>
          {questions.map(
            ({ value, label, type, choices, description, hidden }) =>
              shouldRenderSurveyCard(values, hidden) && (
                <SurveyCard
                  key={value?.toString()}
                  label={value === 'weight' ? 'Weight and Height' : label}
                  value={value?.toString()}
                  type={type}
                  choices={choices}
                  error={isError(value?.toString(), errors, touched)}
                  description={description}
                />
              ),
          )}
          <ButtonLoading
            type="submit"
            variant="primary"
            className="justify-center ml-auto w-full sm:w-max"
            loading={isSubmitting}
          >
            {t('Submit')}
          </ButtonLoading>
        </Form>
      )}
    </Formik>
  );
};
